import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faGaugeHigh, faHeartPulse, IconDefinition} from "@fortawesome/free-solid-svg-icons";
import {Chip} from "@mui/material";
import {DateTime} from "../time/DateTime";

export interface LatestMeterReadings {
    mpan: string;
    value: number;
    lastReading: string;
}

export interface MonitorData {
    lastHeartbeats: { deviceId: string; messageTimeIn: string }[];
    latestMeterReadings: LatestMeterReadings[];
}

export interface BuildingStatusBadgesProps {
    data: MonitorData;
}

type StatusColor = "primary" | "warning" | "error";

const getStatusColor = (time?: string): StatusColor => {
    if (!time) return "error";

    const diffMinutes = DateTime.local().diffMinutes( DateTime.fromISO(time));

    if (diffMinutes <= 10) return "primary";
    else if (diffMinutes <= 60*24) return "warning";
    return "error";
};

export const BuildingStatusBadges = ({ data }: BuildingStatusBadgesProps) => {
    function badge(key: string, statusColour: StatusColor, title = "No device ever seen for the building", label = "never", icon: IconDefinition) {
        return <Chip key={key}
                     data-testid={`dataid-${key}`} variant="outlined" icon={<FontAwesomeIcon
            icon={icon}
            color={statusColour}
            data-testid={`dataid-${key}-icon`}
            size="lg"
        />} label={label} color={statusColour} title={title} className="me-1"/>;
    }

    function formatTime(datestring: string) {
        return DateTime.fromJSDate(new Date(datestring)).toFormat("hh:mm:ss a")
    }

    return (
        <span className="flex gap-3">
            {/* Heartbeats */}
            {data.lastHeartbeats.length > 0 ? (
                data.lastHeartbeats.map((h) => {
                    return (
                        badge(`heartbeat-${h.deviceId}`, getStatusColor(h.messageTimeIn), `Device ${h.deviceId} — last seen at ${h.messageTimeIn}`, formatTime(h.messageTimeIn), faHeartPulse)
                    );
                })
            ) : badge(`heartbeat-error`,"error", "No device ever seen for the building", "never", faHeartPulse)}

            {/* Meter readings */}
            {data.latestMeterReadings.length > 0 ? (
                data.latestMeterReadings.map((r) => (
                    badge(`reading-${r.mpan}`, getStatusColor(r.lastReading), `MPAN ${r.mpan} — last reading at ${r.lastReading}`, formatTime(r.lastReading), faGaugeHigh)
                ))
            ) : badge(`reading-error`, "error", `No meter readings in the last 24 hours`, "over a day", faGaugeHigh)
            }

        </span>
    );
};